import { InputProps, ButtonProps } from '@chakra-ui/react';
import { ChangeEvent, useCallback, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { RoutingPath } from '../../../../routes/routing-path';
import { Api, saveTokenToLocalStorage, getSessionTokenFromLocalStorage, deleteToken } from '../../../../module/api/index';
import RootStore from '../../../../store/root.store';
import { useApiErrorDialog } from '../../../../hooks/utile/use-error-dialog';
import { useDidMount } from '../../../../hooks/utile/use-didmount';
import { useAuth } from '../../../../hooks/auth/use-auth';
import { PasswordChangeDialog } from '../../../ui/dialog/password-change/password-change.dialog';
import { PasswordResetRequestDialog } from '../../../ui/dialog/password-reset-request/password-reset-request.dialog';

export const FORCE_PASSWORD_CHANGE_DIALOG_ID = 'forcePasswordChange';

type ReturnType = {
  idData: InputProps,
  passData: InputProps,
  loginButton: ButtonProps,
  onClickForgotPassword: () => void,
}

export const useLogin = (): ReturnType => {
  const navigate = useNavigate();
  const errDialog = useApiErrorDialog();
  const { logout } = useAuth();
  const [load, setLoad] = useState(false);

  const [id, setId] = useState('');
  const onChangeId = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setId(e.target.value);
  }, []);

  const [password, setPassword] = useState('');
  const onChangePassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  }, []);

  /* 強制パスワード変更の完了・キャンセルはどちらもログアウトしてログイン画面に戻す */
  const backToLogin = useCallback(async() => {
    RootStore.dialog.clear();
    await logout();
    setLoad(false);
  }, [logout]);

  /* 初回ログイン時はパスワードの変更が完了するまで先に進ませない */
  const openForcePasswordChangeDialog = useCallback(() => {
    RootStore.dialog.push({
      id: FORCE_PASSWORD_CHANGE_DIALOG_ID,
      title: 'パスワード変更',
      ignoreClose: true,
      children: <PasswordChangeDialog
        onComplete={backToLogin}
        onCancel={backToLogin}
      />,
    });
  }, [backToLogin]);

  const onClickForgotPassword = useCallback(() => {
    RootStore.dialog.push({
      title: 'パスワードをお忘れの方',
      children: <PasswordResetRequestDialog />,
    });
  }, []);

  const onClickLogin = useCallback(async() => {
    // ログイン処理
    // 画面遷移 企業かISPかで判断して遷移先を変更
    setLoad(true);
    try {
      const loginResponse = await Api.connect().login().post({ login_id: id, password });
      const { token } = loginResponse.data;
      saveTokenToLocalStorage(token);
      const isLoginResponse = await Api.connect().isLogin().get();
      if (isLoginResponse.data.company.password_change_required_flag) {
        openForcePasswordChangeDialog();
        return;
      }
      RootStore.user.setUser(isLoginResponse.data);
      if (isLoginResponse.data.company.system_admin_flag) {
        //ISPログインの時
        navigate(RoutingPath.companies);
      } else {
        //企業ログインの時
        navigate(RoutingPath.articles);
      }
    } catch(e: any) {
      errDialog.push(e, {
        customMessage: ['メールアドレスもしくはパスワードが一致しません'],
      });
      setLoad(false);
    }
  }, [id, password, errDialog, navigate, openForcePasswordChangeDialog]);

  // 自動ログイン処理
  useDidMount(async() => {
    const token = getSessionTokenFromLocalStorage();
    try {
      if (token) {
        const isLoginResponse = await Api.connect().isLogin().get();
        if (isLoginResponse.data.company.password_change_required_flag) {
          openForcePasswordChangeDialog();
          return;
        }
        RootStore.user.setUser(isLoginResponse.data);
        await RootStore.common.initialize();
        if (isLoginResponse.data.company.system_admin_flag) {
          //ISPログインの時
          navigate(RoutingPath.companies);
        } else {
          //企業ログインの時
          navigate(RoutingPath.articles);
        }
      }
    } catch(e) {
      deleteToken();
    }
  })

  return {
    idData: {
      value: id,
      onChange: onChangeId,
      isDisabled: load,
      placeholder: 'ID',
    },
    passData: {
      value: password,
      onChange: onChangePassword,
      type: 'password',
      isDisabled: load,
      placeholder: 'パスワード',
      onKeyDown: (e) => (e.key === 'Enter') && onClickLogin(),
    },
    onClickForgotPassword,
    loginButton: {
      onClick: onClickLogin,
      isDisabled: load || (!id || !password),
      isLoading: load,
      onKeyDown: (e) => {
      }
    }
  }
}